import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { take, map, switchMap, shareReplay, finalize } from 'rxjs/operators';
import { selectPlantsWithBOMByProductListState } from './plants-with-bom-by-product-list.selectors';
import { loadPlantsWithBOMByProductListSuccess, ProductBOM } from './plants-with-bom-by-product-list.actions';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { iPlantsWithBOMList } from 'src/app/pages/orders/order-taking/components/new-order/models/iProductPlantsBOM.model';
import { environment } from 'src/environments/environment';
import { PlantsWithBOMByProductListState } from './plants-with-bom-by-product-list.reducer';

/**
 * Service to fetch and cache Plants with BOM results by product list using NgRx Store.
 * - Checks the store for previously cached results by productId and date.
 * - Only makes API calls for products not present in the cache.
 * - When new results are fetched from the API, they are saved to the store and the full set (cache + new) is returned.
 * - Uses a pending map with shareReplay to deduplicate concurrent calls with the same payload.
 * - The cache is stored as an object indexed by a composite key (productId and date).
 */
@Injectable({ providedIn: 'root' })
export class PlantsWithBOMByProductListApiService {
  /**
   * Base URL for Plants with BOM related API requests.
   */
  private baseUrl: string;

  /**
   * Map of in-flight HTTP requests keyed by normalized product list.
   * Deduplicates concurrent calls with the same payload.
   */
  private pending: { [key: string]: Observable<iPlantsWithBOMList[]> } = {};

  /**
   * HTTP header options for API requests.
   */
  public options = {
    headers: new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Ocp-Apim-Subscription-Key', environment.apimSubscriptionKey),
  };

  /**
   * Service constructor.
   * @param http HttpClient service for API calls.
   * @param store NgRx Store to access and update the cache.
   */
  constructor(
    private http: HttpClient,
    private store: Store,
  ) {
    this.baseUrl = environment.APIM + environment.newOrderAPI;
  }

  /**
   * Builds a normalized key for a list of products (sorted deterministically).
   */
  private buildKey(products: ProductBOM[]): string {
    return JSON.stringify([...products].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  }

  /**
   * Gets the list of plants with BOM for a list of products, using cache and API as needed.
   *
   * - First checks the store to see which products are already cached (by productId and date).
   * - Only missing products are requested from the API.
   * - Concurrent calls with the same payload share a single HTTP request via the pending map.
   * - When the API responds, new results are saved to the store and the full set (cache + new) is returned.
   * - The Observable only emits when all data is ready.
   *
   * @param products List of BOM products to query.
   * @returns Observable with the complete list of results (cached and new).
   */
  getPlantsWithBOMByProductList(products: ProductBOM[]): Observable<iPlantsWithBOMList[]> {
    const normalizeDate = (date: string) => date.split('T')[0];
    const key = this.buildKey(products);

    return this.store.select(selectPlantsWithBOMByProductListState).pipe(
      take(1),
      map((state: PlantsWithBOMByProductListState) => {
        const cacheObj = state.cache || {};
        const cachedResults: iPlantsWithBOMList[] = ([] as iPlantsWithBOMList[]).concat(...Object.values(cacheObj));
        // Filter products NOT in cache
        const productsToFetch = products.filter((prod) => {
          return !cachedResults.some(
            (cached) => cached.productId === prod.productId && normalizeDate(cached.date) === prod.date,
          );
        });
        // Filter products that ARE in cache
        const cachedFiltered = cachedResults.filter((cached) =>
          products.some((prod) => cached.productId === prod.productId && normalizeDate(cached.date) === prod.date),
        );
        return { productsToFetch, cachedFiltered };
      }),
      switchMap(({ productsToFetch, cachedFiltered }) => {
        if (!productsToFetch || productsToFetch.length === 0) {
          return of(cachedFiltered);
        }

        // If there's already an in-flight request for the same key, reuse it
        if (this.pending[key]) {
          return this.pending[key];
        }

        // Create a new HTTP request and share it across concurrent callers
        this.pending[key] = this.http
          .post<iPlantsWithBOMList[]>(`${this.baseUrl}Plant/GetPlantsWithBOMByList`, productsToFetch, this.options)
          .pipe(
            map((apiResults) => {
              this.store.dispatch(loadPlantsWithBOMByProductListSuccess({ products, result: apiResults }));
              return [...cachedFiltered, ...apiResults];
            }),
            shareReplay(1),
            finalize(() => {
              delete this.pending[key];
            }),
          );

        return this.pending[key];
      }),
    );
  }
}
